// Per-feature email senders. Each one owns a single template and a single
// trigger — the browser never chooses a template or an arbitrary recipient.

import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { sendTransactionalEmail } from "./sendTransactionalEmail.server";

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function cleanEmail(value: unknown): string {
  const email = String(value ?? "").trim().toLowerCase();
  if (!emailPattern.test(email)) throw new Error("A valid email address is required");
  return email;
}

/** Referral invite — sent by a signed-in member to the address they typed. */
export const sendReferralInvite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { email: string; referralLink: string; referralCode: string; isLifeMart?: boolean }) => d)
  .handler(async ({ data, context }) => {
    const recipient = cleanEmail(data.email);
    await sendTransactionalEmail({
      templateName: "referral-invite",
      recipientEmail: recipient,
      idempotencyKey: `referral-invite-${context.userId}-${recipient}`,
      templateData: {
        referralLink: data.referralLink,
        referralCode: data.referralCode,
        isLifeMart: Boolean(data.isLifeMart),
      },
    });
    return { ok: true };
  });

/** Focus-group signup confirmation — public form, fixed template. */
export const sendFocusGroupWelcome = createServerFn({ method: "POST" })
  .inputValidator((d: { email: string; name?: string; role?: string }) => d)
  .handler(async ({ data }) => {
    const recipient = cleanEmail(data.email);
    await sendTransactionalEmail({
      templateName: "focus-group-welcome",
      recipientEmail: recipient,
      idempotencyKey: `focus-group-welcome-${recipient}`,
      templateData: { name: data.name, role: data.role },
    });
    return { ok: true };
  });

/** Homie readiness intro — always to the signed-in member's own address. */
export const sendHomieIntro = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { source?: string; zip?: string; totalEstimated?: number }) => d ?? {})
  .handler(async ({ data, context }) => {
    const email = (context.claims as { email?: string } | undefined)?.email;
    if (!email) return { ok: false };
    await sendTransactionalEmail({
      templateName: "homie-intro",
      recipientEmail: email,
      idempotencyKey: `homie-intro-stack-${context.userId}`,
      templateData: {
        source: data?.source ?? "stack",
        zip: data?.zip,
        totalEstimated: data?.totalEstimated ?? 0,
      },
    });
    return { ok: true };
  });

/** Partner lead brief — admin tooling only. */
export const sendPartnerLeadBrief = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { partnerEmail: string; leadId: string; templateData: Record<string, unknown> }) => d)
  .handler(async ({ data, context }) => {
    const { data: isStaff } = await context.supabase.rpc("is_staff_or_admin", {
      _user_id: context.userId,
    });
    if (!isStaff) throw new Error("Forbidden");

    const recipient = cleanEmail(data.partnerEmail);
    const result = await sendTransactionalEmail({
      templateName: "partner-lead-brief",
      recipientEmail: recipient,
      idempotencyKey: `lead-brief-${data.leadId}-${Date.now()}`,
      templateData: data.templateData,
    });
    if (!result.ok) throw new Error("Failed to send brief");
    return { ok: true };
  });
